import * as Yup from "yup";

import {
  CreateTargetRates,
  CreateTargetRatesVariables,
} from "_graphql/mutation/__generated__/CreateTargetRates";
import { Modal, OfficeButton } from "components";

import CreateTargetRateForm from "components/forms/add-target-rate";
import { createTargetRate } from "../../_graphql/visal/mutations/target-rate";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

const rateSchema = Yup.object().shape({
  rate_value: Yup.number()
    .required("Rate value is required")
    .min(0, "Rate value must be at least 0"),
  currency_code: Yup.string().required("Currency code is required"),
  currency: Yup.string().required("Currency name is required"),
});

const validationSchema = Yup.object().shape({
  targetId: Yup.string().required("Target ID is required"),
  rates: Yup.array().of(rateSchema).min(1, "At least one rate is required"),
});

export default function CreateTargetRateContainer({
  open,
  setOpen,
  targetId,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  targetId: string;
  refetch?: () => void;
}) {
  const [execute, { loading }] = useMutation<
    CreateTargetRates,
    CreateTargetRatesVariables
  >(createTargetRate, {
    refetchQueries: ["FetchTargetRates"],
  });

  const form = useFormik<CreateTargetRatesVariables>({
    initialValues: {
      targetId,
      rates: [
        {
          rate_value: 0,
          currency_code: "GHS",
          currency: "Ghana Cedis",
        },
      ],
    },
    validationSchema,
    onSubmit: async (variables, helpers) => {
      try {
        const { data } = await execute({
          variables: {
            targetId: variables.targetId,
            rates: variables.rates,
          },
        });

        if (data?.createTargetRates) {
          toast.success("Target rates configured successfully");
          setOpen(false);
          helpers.resetForm();
          if (refetch) refetch();
        } else {
          toast.error("An error occurred while configuring target rates");
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred";
        toast.error(errorMessage);
      }
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Configure Target Rates"
      description="Set rate values and currency information"
      renderActions={() => (
        <OfficeButton
          type="submit"
          size="sm"
          disabled={loading}
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Please wait..." : "Configure Rates"}
        </OfficeButton>
      )}
    >
      <CreateTargetRateForm form={form} />
    </Modal>
  );
}
